import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient } from '../../api';
import * as types from '../../api/types';
import { cellClass, DateField, EmptyRow, FilterGrid, ReportCard, ReportPage, ReportStatus, ReportTable, SummaryTiles, useReportData, useReportParams } from './ReportParts';
import { FinanceChargeBadge } from '../../components/FinanceChargeBadge';
import { RemindedBadge } from '../../components/DunningControls';
import { agingBucketLabel, dateParam, reportDate, reportMoney } from './reportUi';

const COLUMNS = [
  { label: 'Customer' },
  { label: 'Current', numeric: true }, { label: '1–30', numeric: true }, { label: '31–60', numeric: true },
  { label: '61–90', numeric: true }, { label: '90+', numeric: true },
  { label: 'Outstanding', numeric: true }, { label: 'Unapplied credit', numeric: true }, { label: 'Net balance', numeric: true },
];

function BucketCells({ buckets }: { buckets: types.ARAgingBuckets }) {
  return (
    <>
      <td data-label="Current" className={cellClass(true)}>{reportMoney(buckets.current)}</td>
      <td data-label="1–30" className={cellClass(true)}>{reportMoney(buckets.days1to30)}</td>
      <td data-label="31–60" className={cellClass(true)}>{reportMoney(buckets.days31to60)}</td>
      <td data-label="61–90" className={cellClass(true)}>{reportMoney(buckets.days61to90)}</td>
      <td data-label="90+" className={cellClass(true)}>{reportMoney(buckets.days90plus)}</td>
    </>
  );
}

function CustomerRows({ row, statementQuery }: { row: types.ARAgingCustomerRow; statementQuery: string }) {
  const [expanded, setExpanded] = useState(false);
  const detailId = `aging-invoices-${row.customerId}`;
  return (
    <>
      <tr>
        <td data-label="Customer" data-primary className={cellClass()}>
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              aria-expanded={expanded}
              aria-controls={detailId}
              aria-label={`${expanded ? 'Hide' : 'Show'} invoices for ${row.customerName}`}
              disabled={row.invoices.length === 0}
              onClick={() => setExpanded((value) => !value)}
              className="w-10 min-h-[44px] sm:w-6 sm:min-h-0 shrink-0 text-gray-500 hover:text-gray-900 disabled:opacity-30 disabled:cursor-default"
            >
              {expanded ? '▾' : '▸'}
            </button>
            <Link to={`/customers/${row.customerId}/statement${statementQuery}`} className="min-w-0 break-words font-semibold text-blue-700 hover:underline">{row.customerName}</Link>
          </div>
        </td>
        <BucketCells buckets={row.buckets} />
        <td data-label="Outstanding" className={cellClass(true)}>{reportMoney(row.totalOutstanding)}</td>
        <td data-label="Unapplied credit" className={cellClass(true)}>{reportMoney(row.unappliedCredit)}</td>
        <td data-label="Net balance" className={`${cellClass(true)} font-semibold`}>{reportMoney(row.netBalance)}</td>
      </tr>
      {expanded && (
        <tr id={detailId} className="bg-gray-50">
          <td colSpan={COLUMNS.length} className="px-2 py-3 sm:px-4 sm:pl-12">
            <table className="responsive-table w-full sm:w-auto text-sm">
              <caption className="sr-only">Open invoices for {row.customerName}</caption>
              <thead>
                <tr className="text-gray-600">
                  <th scope="col" className="pr-6 py-1 text-left font-medium">Invoice</th>
                  <th scope="col" className="pr-6 py-1 text-left font-medium">Invoice date</th>
                  <th scope="col" className="pr-6 py-1 text-left font-medium">Due</th>
                  <th scope="col" className="pr-6 py-1 text-right font-medium">Days overdue</th>
                  <th scope="col" className="pr-6 py-1 text-left font-medium">Bucket</th>
                  <th scope="col" className="py-1 text-right font-medium">Remaining</th>
                </tr>
              </thead>
              <tbody>
                {row.invoices.map((invoice) => (
                  <tr key={invoice.invoiceId}>
                    <td data-label="Invoice" data-primary className="pr-6 py-1 flex-wrap"><Link to={`/invoices/${invoice.invoiceId}`} className="text-blue-700 hover:underline break-words">{invoice.invoiceNumber}</Link>{' '}<FinanceChargeBadge invoice={invoice} />{' '}<RemindedBadge step={invoice.dunningStep} /></td>
                    <td data-label="Invoice date" className="pr-6 py-1">{reportDate(invoice.invoiceDate)}</td>
                    <td data-label="Due" className="pr-6 py-1">{reportDate(invoice.dueDate)}</td>
                    <td data-label="Days overdue" className="pr-6 py-1 text-right tabular-nums">{invoice.daysOverdue}</td>
                    <td data-label="Bucket" className="pr-6 py-1">{agingBucketLabel(invoice.bucket)}</td>
                    <td data-label="Remaining" className="py-1 text-right tabular-nums">{reportMoney(invoice.remainingBalance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

export function ARAgingReportPage() {
  const api = useMemo(() => createAPIClient(), []);
  const { params, change } = useReportParams();
  const asOfDate = dateParam(params.get('asOfDate'));
  const state = useReportData(
    `asOf=${asOfDate}`,
    () => api.getARAging({ asOfDate: asOfDate || undefined }),
    'Could not load AR aging',
  );
  // Carry the as-of date into the statement so it ends on the same day.
  const statementQuery = asOfDate ? `?endDate=${asOfDate}` : '';

  return (
    <ReportPage
      title="AR aging"
      intro="Open balances by customer, bucketed by days past the invoice due date. Counts POSTED, PARTIAL and PAID invoices and ACTIVE payments; net balance subtracts unapplied credit. Click a customer for their statement, or the arrow for their open invoices."
    >
      <FilterGrid>
        <DateField id="aging-as-of" label="As of" value={asOfDate} onChange={(value) => change({ asOfDate: value })} hint="Blank means today." />
      </FilterGrid>
      <ReportStatus state={state} label="AR aging">
        {(report) => (
          <>
            <SummaryTiles tiles={[
              { label: 'As of', value: reportDate(report.asOfDate) },
              { label: 'Total outstanding', value: reportMoney(report.totals.totalOutstanding) },
              { label: 'Unapplied credit', value: reportMoney(report.totals.unappliedCredit) },
              { label: 'Net receivable', value: reportMoney(report.totals.netBalance) },
            ]} />
            <ReportCard title="By customer">
              <ReportTable
                columns={COLUMNS}
                caption="AR aging by customer"
                footer={report.customers.length > 0 && (
                  <tr>
                    <td data-label="Customer" data-primary className={cellClass()}>Totals</td>
                    <BucketCells buckets={report.totals} />
                    <td data-label="Outstanding" className={cellClass(true)}>{reportMoney(report.totals.totalOutstanding)}</td>
                    <td data-label="Unapplied credit" className={cellClass(true)}>{reportMoney(report.totals.unappliedCredit)}</td>
                    <td data-label="Net balance" className={cellClass(true)}>{reportMoney(report.totals.netBalance)}</td>
                  </tr>
                )}
              >
                {report.customers.length === 0
                  ? <EmptyRow colSpan={COLUMNS.length} message="Nothing outstanding as of this date." />
                  : report.customers.map((row) => <CustomerRows key={row.customerId} row={row} statementQuery={statementQuery} />)}
              </ReportTable>
            </ReportCard>
          </>
        )}
      </ReportStatus>
    </ReportPage>
  );
}
